import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import "../styles/AgendarCita.css";

import useDisponibilidad from "../hooks/useDisponibilidad";
import useDropdownData from "../hooks/useDropdownData";
import useGuardarCita from "../hooks/useGuardarCita";
import useProveedores from "../hooks/useProveedores";
import useActualizarStatusProveedor from "../hooks/useActualizarStatusProveedor";
import useObtenerCitaPorFecha from "../hooks/useObtenerCitaPorFecha";

import {
  combinarFechaYHora,
  construirFechaSinHoraExtra,
  urlApi,
  obtenerCorreosPorEmpresa,
} from "../helpers/functionHelpers";
import { useAlert } from "../context/AlertContext";
import useEmail from "../hooks/useEmail";
import { emailCitaConfirmada } from "../utils/email/emailCitaConfirmada";
import useEmpresaStore from "../context/empresaStore";
import SelectAlmacen from "./SelectAlmacen";

const AgendarCita = () => {
  const { folio } = useParams();
  const { empresa_id } = useEmpresaStore();
  const navigate = useNavigate();
  const [almacen, setAlmacen] = useState({
    id: "",
    ubicacion: "",
    descripcion: "",
  });
  const [dataCita, setDataCita] = useState({
    cab_id: "",
    fechaRsv: "",
    dataIni: "",
    dataFin: "",
    comentariosAdAlmcn: "",
    comentariosAdCrr: "",
    AlmacenAsignado_id: "",
  });
  const [duracion, setDuracion] = useState("");
  const [horariosOcupados, setHorariosOcupados] = useState([]);
  const [horarioSeleccionado, setHorarioSeleccionado] = useState("");
  const [calendarStartDate, setCalendarStartDate] = useState(null);
  const [transportista, setTransportista] = useState({
    cab_id: "",
    nameOpr: "",
    telefonoOpr: "",
    vehiculo_id: "",
    placa: "",
    numOprUdad: "",
    personalAdcnl: "",
    cometariosTrp: "",
    correoTrp: "",
  });
  const [proveedorEmail, setProveedorEmail] = useState({
    correoPrv: "",
    id: "",
  });
  const [botonDeshabilitado, setBotonDeshabilitado] = useState(false);

  const duraciones = [
    "15 minutos",
    "30 minutos",
    "45 minutos",
    "1 hora",
    "1.5 horas",
    "2 horas",
    "2.5 horas",
    "3 horas",
    "4 horas",
  ];

  const fecha = dataCita.fechaRsv
    ? construirFechaSinHoraExtra(dataCita.fechaRsv)
    : null;

  const { disponibilidad } = useDisponibilidad(duracion, horariosOcupados);
  const {
    data: ubicacions,
    loading: loadingUbicacions,
    error: errorUbicacions,
  } = useDropdownData(`${urlApi}/api/ubicacions`, setAlmacen, almacen.id, true);
  const {
    guardarCita,
    error: errorGuardarCita,
    loading: loadingGuardarCita,
  } = useGuardarCita();
  const { proveedores: dataTransportista } = useProveedores(
    `${urlApi}/api/transportista/${folio}`
  );
  const { proveedores: dataProveedores } = useProveedores(
    `${urlApi}/api/proveedores/${folio}`
  );

  const { actualizarStatusProveedor } = useActualizarStatusProveedor();
  const {
    citas,
    loading: loadingCitas,
    error: errorCitas,
  } = useObtenerCitaPorFecha(almacen.id, fecha);
  const { sendEmail } = useEmail();
  const { mostrarAlerta } = useAlert();

  const obtenerDescripcion = (id, lista) => {
    return lista.find((element) => element.id === id) || null;
  };

  const cargarTransportista = (transportista) => {
    setTransportista((prevState) => ({
      ...prevState,
      cab_id: transportista.cab_id,
      nameOpr: transportista.nameOpr,
      telefonoOpr: transportista.telefonoOpr,
      vehiculo_id: transportista.vehiculo_id,
      placa: transportista.placa,
      numOprUdad: transportista.numOprUdad,
      personalAdcnl: transportista.personalAdcnl,
      cometariosTrp: transportista.comentariosTrp,
      correoTrp: transportista.correoTrp,
    }));
  };

  const cargarCorreoProveedor = (proveedor) => {
    if (proveedor) {
      setProveedorEmail({
        correoPrv: proveedor.correoPrv,
        id: proveedor.id,
      });
      if (!dataCita.fechaRsv && proveedor.fechaRsv) {
        const fechaProveedorString = String(proveedor.fechaRsv).slice(0, 10);
        const [year, month, day] = fechaProveedorString.split("-").map(Number);
        const fechaProveedor = new Date(year, month - 1, day);
        setDataCita((prevState) => ({
          ...prevState,
          fechaRsv: fechaProveedor,
        }));
        setCalendarStartDate(fechaProveedor);
      }
    }
  };

  const handleHorarioSeleccionado = (slot) => {
    const [horaInicio, horaFin] = slot.split(" - ");
    setDataCita((prevData) => ({
      ...prevData,
      cab_id: transportista.cab_id,
      dataIni: horaInicio,
      dataFin: horaFin,
      comentariosAdAlmcn: "a",
      comentariosAdCrr: "a",
    }));
    setHorarioSeleccionado(slot);
  };

  const handleAlmacenChange = useCallback((id) => {
  const almacenObj = ubicacions.find((u) => u.id === Number(id)) || {
    id,
    descripcion: "",
    ubicacion: "",
  };

  setAlmacen(almacenObj);
  setDataCita((prev) => ({
    ...prev,
    AlmacenAsignado_id: id || "",
  }));
}, [ubicacions]);

  const handleDateChange = (date) => {
    setDataCita((prevData) => ({
      ...prevData,
      fechaRsv: date,
    }));
  };

  const handleDuracionChange = (e) => {
    const selectedDuration = e.target.value;
    setDuracion(selectedDuration);
  };

  const sendNotification = (
    folio,
    fecha,
    horaIni,
    horaFin,
    nombreAlmacen,
    direccion,
    mapaURL
  ) => {
    const message = emailCitaConfirmada({
      fecha,
      horaIni,
      horaFin,
      duracion,
      nombreAlmacen,
      direccion,
      mapaURL,
      urlApi,
      folio,
      empresa_id,
    });
    const subject = `Actualización de la cita con folio ${folio} para el sistema almacenes Sanfer`;
    const toEmail = [
      `${proveedorEmail?.correoPrv}`,
      `${transportista?.correoTrp}`,
    ];
    const emails = obtenerCorreosPorEmpresa(empresa_id, almacen?.descripcion);
    sendEmail({ to: toEmail.join(","), subject, html: message, bcc: emails });
  };

  const handleGuardarCita = async () => {
    if (loadingGuardarCita) return;

    if (!dataCita.AlmacenAsignado_id && almacen?.id) {
    setDataCita((prevData) => ({
      ...prevData,
      AlmacenAsignado_id: almacen.id
    }));
  }
    setBotonDeshabilitado(true);
    if (dataCita.fechaRsv) {
      const fechaSinFormato = construirFechaSinHoraExtra(dataCita.fechaRsv);
      const fechaIn = combinarFechaYHora(dataCita.fechaRsv, dataCita.dataIni);
      const fechaFin = combinarFechaYHora(dataCita.fechaRsv, dataCita.dataFin);
      const url = `${urlApi}/api/citas`;
      const ubicacion = obtenerDescripcion(
        Number(dataCita.AlmacenAsignado_id),
        ubicacions
      );

      const nombreAlmacen = ubicacion?.descripcion || "No disponible";
      const direccion = ubicacion?.direccion || "Dirección no registrada";
      const mapaUrl = ubicacion?.mapa_url || "";

      const respuesta = await guardarCita(
        dataCita.AlmacenAsignado_id,
        dataCita.cab_id,
        fechaSinFormato,
        fechaIn,
        fechaFin,
        dataCita.comentariosAdAlmcn,
        dataCita.comentariosAdCrr,
        url
      );

      if (respuesta) {
        mostrarAlerta("Cita guardada con éxito", "success");
        await actualizarStatusProveedor(folio, 30);
        await sendNotification(
          folio,
          fechaSinFormato,
          fechaIn,
          fechaFin,
          nombreAlmacen,
          direccion,
          mapaUrl
        );
        setTimeout(() => {
          navigate(`/admin/pendientes`);
        }, 2000);
      } else {
        mostrarAlerta(`Hubo un problema al generar la reservación`, "error");
        setBotonDeshabilitado(false);
      }
    } else {
      mostrarAlerta("Ingresa una fecha", "error");
      setBotonDeshabilitado(false);
    }
  };
  const transportistaApi = Array.isArray(dataTransportista)
    ? dataTransportista[0]
    : dataTransportista;
  const proveedorApi = Array.isArray(dataProveedores)
    ? dataProveedores[0]
    : dataProveedores;

  //useEffect para cargar la data del transportista y el correo del proveedor
  useEffect(() => {
    if (transportistaApi) {
      cargarTransportista(transportistaApi);
    }
    if (proveedorApi) {
      cargarCorreoProveedor(proveedorApi);
    }
  }, [transportistaApi, proveedorApi]);

  //useEffect para cargar los horarios ocupados
  useEffect(() => {
    if (dataCita.AlmacenAsignado_id && dataCita.fechaRsv) {
      const horarios = citas.map((cita) => ({
        inicio: new Date(cita.dataIni).toISOString(),
        fin: new Date(cita.dataFin).toISOString(),
      }));
      setHorariosOcupados(horarios);
    }
  }, [dataCita.AlmacenAsignado_id, dataCita.fechaRsv, citas]);

  return (
    <div>
      <div className="agendar-cita-container">
        <div className="calendar-section">
          <Calendar
            onChange={handleDateChange}
            value={dataCita.fechaRsv}
            activeStartDate={calendarStartDate || undefined}
            onActiveStartDateChange={({ activeStartDate }) => setCalendarStartDate(activeStartDate)}
          />
        </div>
        <div className="form-section">
          <div className="form-group">
            <label>Almacen:</label>
            {proveedorEmail?.id && (
              <SelectAlmacen
                loading={loadingUbicacions}
                error={errorUbicacions}
                ubicacions={ubicacions}
                almacenSeleccionado={almacen.id}
                onAlmacenChange={handleAlmacenChange}
                proveedorId={proveedorEmail.id}
              />
            )}
          </div>
          <div className="form-group">
            <label>Duración:</label>
            <select
              id="duracion"
              name="duracion"
              value={duracion}
              onChange={handleDuracionChange}
            >
              <option value="Seleccione">Seleccione</option>
              {duraciones.map((duracion, index) => (
                <option key={index} value={duracion}>
                  {duracion}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Disponibilidad:</label>
            <div className="horarios-disponibles" id="disponibilidad">
              {loadingCitas ? (
                <p>Cargando horarios...</p>
              ) : errorCitas ? (
                <p>Error al cargar horarios: {errorCitas}</p>
              ) : disponibilidad.length > 0 ? (
                disponibilidad.map((slot, index) => (
                  <motion.button
                    key={index}
                    className={`horario-slot ${
                      horariosOcupados.some(
                        (horario) =>
                          new Date(slot.inicio) >= new Date(horario.inicio) &&
                          new Date(slot.fin) <= new Date(horario.fin)
                      )
                        ? "ocupado"
                        : horarioSeleccionado === slot
                        ? "seleccionado"
                        : ""
                    }`}
                    onClick={() => handleHorarioSeleccionado(slot)}
                    whileTap={{ scale: 0.95 }}
                    transition={{ type: "spring", stiffness: 500, damping: 20 }}
                  >
                    {slot}
                  </motion.button>
                ))
              ) : (
                <p>No hay horarios disponibles para la selección actual.</p>
              )}
            </div>
          </div>
          <motion.button
            type="button"
            className="agendar-btn"
            onClick={handleGuardarCita}
            disabled={botonDeshabilitado}
            whileTap={{ scale: 0.95 }}
            transition={{ type: "spring", stiffness: 500, damping: 20 }}
          >
            {loadingGuardarCita ? "Guardando..." : "Guardar Cita"}
          </motion.button>
          {errorGuardarCita && (
            <p style={{ color: "red" }}>{errorGuardarCita}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AgendarCita;
